// Route: /weak-spots — карта слабых мест: приоритизированный список тем-
// пробелов с серьёзностью, прогрессом закрытия и прямым CTA.
//
// URL state: ?status, ?severity, ?fromAttempt
// tRPC: weakSpots.list. См. docs/ui-spec/weak-spots.list.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState, LoadingState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import {
	IconArrowLeft,
	IconCircleCheck,
	IconFileText,
	IconList,
	IconTarget,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LIST_PAGE_SHELL, LIST_PAGE_STACK, ListToolbar } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import {
	useMaterialTitles,
	useWeakSpots,
	type WeakSpot,
	type WeakSpotSeverity,
} from "@/components/weak-spots/_api";
import { useWeakSpotTopicLinks } from "@/components/weak-spots/use-topic-links";
import { WeakSpotCard } from "@/components/weak-spots/weak-spot-card";
import { WeakSpotsGroup } from "@/components/weak-spots/weak-spots-group";
import { WeakSpotsSummary } from "@/components/weak-spots/weak-spots-summary";

/** Порядок серьёзности — сначала самые острые пробелы. */
const SEVERITY_ORDER: Record<WeakSpotSeverity, number> = {
	high: 0,
	medium: 1,
	low: 2,
};

/** Ключ группы для пробелов без материала (либо с недоступным материалом). */
const UNASSIGNED_KEY = "__unassigned__";

type WeakSpotsViewMode = "byMaterial" | "list";
const VIEW_STORAGE_KEY = "studyhub:weak-spots:view";

function readStoredView(): WeakSpotsViewMode {
	try {
		const v = localStorage.getItem(VIEW_STORAGE_KEY);
		return v === "list" || v === "byMaterial" ? v : "byMaterial";
	} catch {
		return "byMaterial";
	}
}

const searchSchema = z.object({
	status: z.enum(["active", "resolving", "resolved", "all"]).optional(),
	severity: z.enum(["low", "medium", "high"]).optional(),
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
	/** ID попытки теста, с результатов которой пришли — для кнопки «Назад». */
	fromAttempt: z.string().uuid().optional(),
});

export const Route = createFileRoute("/weak-spots")({
	validateSearch: tolerantSearch(searchSchema),
	component: WeakSpotsPage,
	pendingComponent: PagePendingFallback,
});

/** Группа пробелов одного материала: ключ + название + отсортированные spots. */
interface SpotGroup {
	key: string;
	title: string | null;
	spots: WeakSpot[];
	/** Минимальный SEVERITY_ORDER в группе — для сортировки групп. */
	topSeverity: number;
}

function WeakSpotsPage(): React.ReactElement {
	const { t } = useTranslation("weak-spots");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const intro = useSectionIntro("weak-spots");

	const status = search.status ?? "active";
	const query = useWeakSpots({
		status,
		severity: search.severity,
		demo: search.demo,
	});
	const titlesQuery = useMaterialTitles();

	// Поиск по теме + режим отображения (По материалам / Список).
	const [searchValue, setSearchValue] = useState("");
	const [debounced, setDebounced] = useState("");
	const [viewMode, setViewMode] = useState<WeakSpotsViewMode>(readStoredView);

	useEffect(() => {
		const handle = window.setTimeout(() => setDebounced(searchValue.trim().toLowerCase()), 200);
		return () => window.clearTimeout(handle);
	}, [searchValue]);

	function changeViewMode(next: WeakSpotsViewMode): void {
		setViewMode(next);
		try {
			localStorage.setItem(VIEW_STORAGE_KEY, next);
		} catch {
			/* localStorage недоступен — режим живёт только в сессии */
		}
	}

	const rawItems = useMemo(() => query.data?.items ?? [], [query.data]);
	const titles = useMemo(() => titlesQuery.data ?? new Map<string, string>(), [titlesQuery.data]);

	/** Фильтрация по строке поиска (по теме пробела). */
	const items = useMemo(() => {
		if (debounced.length === 0) return rawItems;
		return rawItems.filter((s) => s.topic.toLowerCase().includes(debounced));
	}, [rawItems, debounced]);

	/**
	 * Карта «тема пробела → тема курса» (§9): одна PowerSync-подписка на весь
	 * список вместо watch'а в каждой карточке. Даёт прямой вход в шаг ③.
	 */
	const topicLinks = useWeakSpotTopicLinks(useMemo(() => items.map((s) => s.topic), [items]));

	/** Активные пробелы (не закрытые) — для сводной панели по серьёзности. */
	const activeSpots = useMemo(() => items.filter((s) => s.status !== "resolved"), [items]);
	const resolvedCount = useMemo(() => items.filter((s) => s.status === "resolved").length, [items]);

	/**
	 * Группировка по материалу + сортировка:
	 *  - внутри группы пробелы по серьёзности (high → low);
	 *  - группы по самому острому пробелу внутри них;
	 *  - пробелы без материала — отдельной группой в конце.
	 *
	 * Корень бага «два блока ‹Без привязки к материалу›»: ключ группировки
	 * брался как сырой `materialId`, но если материал недоступен (удалён или
	 * не попал в первые 100 из useMaterialTitles), его title резолвился в null
	 * и группа рендерилась с тем же заголовком «Без привязки», что и настоящая
	 * `__unassigned__`-группа — получалось ДВА разных блока с одинаковой шапкой.
	 * Фикс: нормализуем ключ — пробел с пустым materialId ИЛИ нерезолвящимся
	 * названием всегда попадает в единственную `__unassigned__`-группу.
	 */
	const groups = useMemo<SpotGroup[]>(() => {
		const byMaterial = Map.groupBy(items, (s) => {
			if (!s.materialId) return UNASSIGNED_KEY;
			// Материал есть, но название недоступно → склеиваем с «Без привязки».
			return titles.has(s.materialId) ? s.materialId : UNASSIGNED_KEY;
		});
		const result: SpotGroup[] = [];
		for (const [key, spots] of byMaterial) {
			const sorted = [...spots].sort(
				(a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
			);
			result.push({
				key,
				title: key === UNASSIGNED_KEY ? null : (titles.get(key) ?? null),
				spots: sorted,
				topSeverity: Math.min(...sorted.map((s) => SEVERITY_ORDER[s.severity])),
			});
		}
		return result.sort((a, b) => {
			// Группа без материала — всегда последней.
			if (a.key === UNASSIGNED_KEY) return 1;
			if (b.key === UNASSIGNED_KEY) return -1;
			return a.topSeverity - b.topSeverity;
		});
	}, [items, titles]);

	/** Плоский список (режим «Список») — пробелы по серьёзности без секций. */
	const flatSpots = useMemo(
		() => [...items].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
		[items],
	);

	const isLoading = query.isLoading;
	const isError = query.isError;
	// Пусто после фильтрации поиском — отдельное состояние (есть данные, но не совпало).
	const isEmptyAll = !isLoading && !isError && rawItems.length === 0;
	const isEmptySearch = !isLoading && !isError && rawItems.length > 0 && items.length === 0;

	function setSeverity(severity: WeakSpotSeverity | undefined): void {
		void navigate({
			search: (prev) => ({ ...prev, severity }),
			replace: true,
		});
	}

	function setStatus(next: typeof status): void {
		void navigate({
			search: (prev) => ({ ...prev, status: next }),
			replace: true,
		});
	}

	return (
		<div className={LIST_PAGE_SHELL}>
			{search.fromAttempt && (
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="-ml-2 cursor-pointer text-muted-foreground"
				>
					<Link to="/tests/results/$attemptId" params={{ attemptId: search.fromAttempt }}>
						<IconArrowLeft size={14} className="mr-1" />
						{t("back")}
					</Link>
				</Button>
			)}

			<SectionHeader
				category="quiz"
				icon={<IconTarget />}
				title={t("title")}
				description={t("subtitle")}
				actions={<SectionIntroReopen intro={intro} />}
			/>

			{/* Баннер-объяснение раздела: текст приходит из манифеста модуля,
			    состояние «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Сводная панель — обзор + фильтр по серьёзности.
			    Показываем только при наличии данных и не в ошибке. */}
			{!isLoading && !isError && !isEmptyAll && (
				<WeakSpotsSummary
					activeSpots={activeSpots}
					severityFilter={search.severity}
					onSeverityToggle={setSeverity}
					resolvedCount={resolvedCount}
				/>
			)}

			{/* Единый тулбар «Картотека»: поиск + сегмент «По материалам/Список» +
			    статус-фильтр в доп.фильтрах. Своего grid/list-вида карточек нет →
			    showViewToggle отключён (режим = группировка, не плотность). */}
			{!isLoading && !isError && (
				<ListToolbar
					search={{ value: searchValue, onChange: setSearchValue, placeholder: t("search") }}
					showViewToggle={false}
					segments={
						<SegmentedControl
							scrollable
							value={viewMode}
							onValueChange={(v) => changeViewMode(v as WeakSpotsViewMode)}
							options={[
								{
									value: "byMaterial",
									label: t("view.byMaterial"),
									icon: <IconFileText size={16} stroke={1.75} />,
								},
								{
									value: "list",
									label: t("view.list"),
									icon: <IconList size={16} stroke={1.75} />,
								},
							]}
						/>
					}
					filtersActive={status !== "active" || Boolean(search.severity)}
					filters={
						<StatusFilter status={status} severity={search.severity} onStatusChange={setStatus} />
					}
					filtersSheet={
						<div className="flex flex-col gap-3">
							<StatusFilter
								fullWidth
								status={status}
								severity={search.severity}
								onStatusChange={setStatus}
							/>
							{search.severity && (
								<Button
									variant="ghost"
									className="cursor-pointer self-start text-muted-foreground"
									onClick={() => setSeverity(undefined)}
								>
									{t("clearSeverity", { level: t(`severity.${search.severity}`) })}
								</Button>
							)}
						</div>
					}
				/>
			)}

			{/* Ошибка */}
			{isError && (
				<ErrorState
					title={t("errorTitle")}
					description={t("errorDescription")}
					action={
						<Button
							variant="outline"
							onClick={() => void query.refetch()}
							className="cursor-pointer"
						>
							{t("retry")}
						</Button>
					}
				/>
			)}

			{/* Загрузка — скелетоны с радиусом/тенью реальных карточек */}
			{isLoading && <LoadingState rows={4} rowClassName="h-36" />}

			{/* Пусто после поиска — есть данные, но запрос ничего не нашёл */}
			{isEmptySearch && (
				<EmptyState
					title={t("emptySearchTitle")}
					description={t("emptySearchDescription")}
					icon={<IconTarget />}
					action={
						<Button variant="outline" onClick={() => setSearchValue("")} className="cursor-pointer">
							{t("emptySearchAction")}
						</Button>
					}
				/>
			)}

			{/* Пустое состояние — «нет слабых мест» это успех, не пустота */}
			{isEmptyAll && (
				<EmptyState
					title={
						status === "resolved"
							? t("emptyResolvedTitle")
							: search.severity
								? t("emptyFilterTitle")
								: t("emptySuccessTitle")
					}
					description={
						status === "resolved"
							? t("emptyResolvedDescription")
							: search.severity
								? t("emptyFilterDescription")
								: t("emptySuccessDescription")
					}
					icon={search.severity || status === "resolved" ? <IconTarget /> : <IconCircleCheck />}
					action={
						search.severity ? (
							<Button
								variant="outline"
								onClick={() => setSeverity(undefined)}
								className="cursor-pointer"
							>
								{t("emptyFilterAction")}
							</Button>
						) : (
							<Button asChild className="cursor-pointer">
								<Link to="/tests">{t("emptySuccessAction")}</Link>
							</Button>
						)
					}
				/>
			)}

			{/* Контент. По материалам — секции-аккордеоны; Список — плоско. */}
			{!isLoading &&
				!isError &&
				!isEmptyAll &&
				!isEmptySearch &&
				(viewMode === "byMaterial" ? (
					<div className="space-y-6">
						{groups.map((group) => (
							<WeakSpotsGroup
								key={group.key}
								materialTitle={group.title}
								spots={group.spots}
								topicLinks={topicLinks}
							/>
						))}
					</div>
				) : (
					<div className={LIST_PAGE_STACK}>
						{flatSpots.map((spot) => {
							const link = topicLinks.get(spot.topic);
							return (
								<WeakSpotCard key={spot.id} spot={spot} {...(link ? { topicLink: link } : {})} />
							);
						})}
					</div>
				))}
		</div>
	);
}

interface StatusFilterProps {
	status: "active" | "resolving" | "resolved" | "all";
	severity: WeakSpotSeverity | undefined;
	onStatusChange: (next: "active" | "resolving" | "resolved" | "all") => void;
	fullWidth?: boolean;
}

/** Сегмент-фильтр статуса (Активные / В работе / Закрытые / Все). */
function StatusFilter({
	status,
	onStatusChange,
	fullWidth,
}: StatusFilterProps): React.ReactElement {
	const { t } = useTranslation("weak-spots");
	return (
		<SegmentedControl
			scrollable
			fullWidth={fullWidth}
			value={status}
			onValueChange={(v) => onStatusChange(v as StatusFilterProps["status"])}
			options={[
				{ value: "active", label: t("active") },
				{ value: "resolving", label: t("resolving") },
				{ value: "resolved", label: t("resolved") },
				{ value: "all", label: t("all") },
			]}
		/>
	);
}
